import { useState } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Logo } from '@/components/ui/Logo'
import type { UserPrimaryRole } from '@/types'

interface NavItem { label: string; to: string; icon: string }

const SCHOOL_NAV: NavItem[] = [
  { label: 'Overview',    to: '/app/school',             icon: '▦' },
  { label: 'Instructors', to: '/app/school/instructors', icon: '◎' },
  { label: 'Students',    to: '/app/school/students',    icon: '⊙' },
  { label: 'Reports',     to: '/app/school/reports',     icon: '▤' },
  { label: 'Settings',    to: '/app/school/settings',    icon: '⚙' },
]
const INSTRUCTOR_NAV: NavItem[] = [
  { label: 'Dashboard',   to: '/app/instructor',          icon: '▦' },
  { label: 'Enroll',      to: '/app/instructor/enroll',   icon: '+' },
  { label: 'Students',    to: '/app/instructor/students', icon: '⊙' },
  { label: 'Library',     to: '/app/instructor/library',  icon: '▤' },
  { label: 'Mock Test',   to: '/app/instructor/mock',     icon: '✎' },
  { label: 'Settings',    to: '/app/instructor/settings', icon: '⚙' },
]
const STUDENT_NAV: NavItem[] = [
  { label: 'Home',        to: '/app/student',             icon: '▦' },
  { label: 'Course',      to: '/app/student/course',      icon: '▤' },
  { label: 'Checklist',   to: '/app/student/checklist',   icon: '☑' },
  { label: 'Mock Test',   to: '/app/student/mock',        icon: '✎' },
  { label: 'Certificate', to: '/app/student/certificate', icon: '★' },
  { label: 'Settings',    to: '/app/student/settings',    icon: '⚙' },
]

const NAV_MAP: Record<UserPrimaryRole, NavItem[]> = {
  school_admin: SCHOOL_NAV,
  instructor:   INSTRUCTOR_NAV,
  student:      STUDENT_NAV,
}

interface AppShellProps { role: UserPrimaryRole }

export default function AppShell({ role }: AppShellProps) {
  const navigate = useNavigate()
  const { profile, signOut } = useAuth()
  const [mobOpen, setMobOpen] = useState(false)

  const navItems = NAV_MAP[role]

  const handleSignOut = async () => {
    await signOut()
    navigate('/login', { replace: true })
  }

  const initials = profile?.full_name
    ? profile.full_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : '?'

  return (
    <div className='app-wrap'>
      {/* Desktop sidebar */}
      <aside className='app-sidebar'>
        <div style={{ marginBottom: 32 }}><Logo size={24} light /></div>

        <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === navItems[0].to}
              style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: 11,
                padding: '9px 12px', borderRadius: 9,
                font: '500 13.5px Inter, sans-serif',
                color: isActive ? '#fff' : 'rgba(255,255,255,0.55)',
                background: isActive ? 'rgba(255,255,255,0.12)' : 'transparent',
                textDecoration: 'none', transition: 'all 0.12s',
              })}
            >
              <span style={{ fontSize: 14, width: 18, textAlign: 'center' }}>{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* User / sign out */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%', background: 'rgba(15,124,108,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              font: '600 12px Inter, sans-serif', color: '#fff', flexShrink: 0,
            }}>
              {initials}
            </div>
            <div style={{ overflow: 'hidden' }}>
              <div style={{ font: '600 12.5px Inter, sans-serif', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {profile?.full_name ?? 'Loading…'}
              </div>
              <div style={{ font: '400 11px Inter, sans-serif', color: 'rgba(255,255,255,0.45)', marginTop: 1 }}>
                {role.replace('_', ' ')}
              </div>
            </div>
          </div>
          <button
            onClick={handleSignOut}
            style={{
              width: '100%', padding: '8px 12px', background: 'rgba(255,255,255,0.07)',
              border: 'none', borderRadius: 8, font: '500 12.5px Inter, sans-serif',
              color: 'rgba(255,255,255,0.6)', cursor: 'pointer', textAlign: 'left',
            }}
          >
            Sign out
          </button>
        </div>
      </aside>

      {/* Mobile topbar */}
      <div className='app-mob-topbar' style={{ padding: '14px 16px', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 50 }}>
        <Logo size={22} light />
        <button
          onClick={() => setMobOpen(o => !o)}
          style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, padding: 4 }}
        >
          {mobOpen ? '✕' : '☰'}
        </button>

        {/* Mobile nav drawer */}
        {mobOpen && (
          <div style={{
            position: 'fixed', inset: 0, top: 52, background: 'rgba(27,42,74,0.97)',
            zIndex: 100, padding: '16px 16px', display: 'flex', flexDirection: 'column', gap: 4,
          }}>
            {navItems.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === navItems[0].to}
                onClick={() => setMobOpen(false)}
                style={({ isActive }) => ({
                  display: 'flex', alignItems: 'center', gap: 11, padding: '11px 14px', borderRadius: 9,
                  font: '500 14px Inter, sans-serif',
                  color: isActive ? '#fff' : 'rgba(255,255,255,0.6)',
                  background: isActive ? 'rgba(255,255,255,0.12)' : 'transparent',
                  textDecoration: 'none',
                })}
              >
                <span style={{ fontSize: 15, width: 20, textAlign: 'center' }}>{item.icon}</span>
                {item.label}
              </NavLink>
            ))}
            <button
              onClick={handleSignOut}
              style={{
                marginTop: 8, padding: '11px 14px', background: 'rgba(255,255,255,0.07)',
                border: 'none', borderRadius: 9, font: '500 14px Inter, sans-serif',
                color: 'rgba(255,255,255,0.6)', cursor: 'pointer', textAlign: 'left',
              }}
            >
              Sign out
            </button>
          </div>
        )}
      </div>

      {/* Main content */}
      <main className='app-content'>
        <Outlet />
      </main>
    </div>
  )
}
